import { SETTLED_WORKFLOW_STATUS_CODES } from "../status/displayStatusColors";
import type { ClockingCell, ClockingGridModel } from "../types";

/** Clean punches — never shown in the clear queue. */
const HIDDEN_STATUSES = new Set(["complete", "clocked_in", "time_off"]);

/** True when review workflow is done (status 4 / 5 / 6). */
export function isSettledAttendanceCell(cell: Pick<ClockingCell, "status">): boolean {
  const statusCode = Number(cell.status);
  return Number.isFinite(statusCode) && SETTLED_WORKFLOW_STATUS_CODES.has(statusCode);
}

export type ExceptionInboxItem = {
  key: string;
  attendanceId: number;
  resourceId: number;
  resourceName: string;
  role: string | null;
  groupTitle: string;
  date: string;
  displayStatus: string;
  displayStatusLabel: string;
  clockInAt: string | null;
  clockOutAt: string | null;
  /** False when already reviewed/excused/corrected — shown but not bulk-selectable. */
  actionable: boolean;
};

/** Flatten attendance rows for the clear queue (open + recently settled). */
export function buildExceptionInbox(
  model: ClockingGridModel | null | undefined,
  dateFilter?: string | null
): ExceptionInboxItem[] {
  if (!model?.groups.length) return [];

  const items: ExceptionInboxItem[] = [];

  for (const group of model.groups) {
    for (const row of group.rows) {
      for (const cell of row.cells) {
        if (cell.attendanceId == null) continue;
        if (dateFilter && cell.date !== dateFilter) continue;

        const status = cell.displayStatus ?? "";
        if (HIDDEN_STATUSES.has(status)) continue;
        if (!status && !cell.statusLabel) continue;

        items.push({
          key: `att-${cell.attendanceId}`,
          attendanceId: cell.attendanceId,
          resourceId: row.resourceId,
          resourceName: row.name,
          role: row.role,
          groupTitle: group.title,
          date: cell.date,
          displayStatus: status || "needs_review",
          displayStatusLabel: cell.displayStatusLabel || cell.statusLabel || "Needs review",
          clockInAt: cell.clockInAt,
          clockOutAt: cell.clockOutAt,
          actionable: !isSettledAttendanceCell(cell),
        });
      }
    }
  }

  items.sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    // Open work first within a day, then settled.
    if (a.actionable !== b.actionable) return a.actionable ? -1 : 1;
    return a.resourceName.localeCompare(b.resourceName);
  });

  return items;
}

export function countActionableExceptions(items: ExceptionInboxItem[]): number {
  return items.filter((item) => item.actionable).length;
}
